import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { propertiesApi, wilayasApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Home, Building2, MapPin, Camera, Check, ArrowLeft, ArrowRight,
  Maximize2, BedDouble, Bath, Car, Layers, X
} from 'lucide-react';

export const AddPropertyPage = () => {
  const { t, language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [wilayas, setWilayas] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: '',
    transaction_type: '',
    price: '',
    surface: '',
    rooms: '',
    bedrooms: '',
    bathrooms: '',
    floor: '',
    total_floors: '',
    has_parking: false,
    has_elevator: false,
    has_balcony: false,
    has_garden: false,
    year_built: '',
    city: '',
    neighborhood: '',
    address: '',
    latitude: '',
    longitude: '',
    images: [],
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error(language === 'fr' ? 'Connectez-vous pour déposer une annonce' : 'سجل الدخول لنشر إعلان');
      navigate('/login');
    }
    loadWilayas();
  }, [isAuthenticated, navigate]);

  const loadWilayas = async () => {
    try {
      const response = await wilayasApi.list();
      setWilayas(response.data || []);
    } catch (error) {
      console.error('Failed to load wilayas:', error);
    }
  };

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleImageAdd = () => {
    const url = prompt(language === 'fr' ? 'URL de l\'image:' : 'رابط الصورة:');
    if (url) {
      setFormData({ ...formData, images: [...formData.images, url] });
    }
  };

  const handleImageRemove = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        surface: parseFloat(formData.surface),
        rooms: formData.rooms ? parseInt(formData.rooms) : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        floor: formData.floor ? parseInt(formData.floor) : null,
        total_floors: formData.total_floors ? parseInt(formData.total_floors) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      };
      
      const response = await propertiesApi.create(data);
      toast.success(language === 'fr' ? 'Annonce publiée avec succès !' : 'تم نشر الإعلان بنجاح!');
      navigate(`/property/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la publication' : 'خطأ أثناء النشر'));
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.property_type && formData.transaction_type;
      case 2:
        return formData.title && formData.description && formData.price && formData.surface;
      case 3:
        return formData.city && formData.neighborhood;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const steps = [
    { num: 1, label: t('add_step1'), icon: Home },
    { num: 2, label: t('add_step2'), icon: Layers },
    { num: 3, label: t('add_step3'), icon: MapPin },
    { num: 4, label: t('add_step4'), icon: Camera },
  ];

  const propertyTypes = [
    { value: 'appartement', label: language === 'fr' ? 'Appartement' : 'شقة', icon: Building2 },
    { value: 'maison', label: language === 'fr' ? 'Maison' : 'منزل', icon: Home },
    { value: 'villa', label: language === 'fr' ? 'Villa' : 'فيلا', icon: Home },
    { value: 'terrain', label: language === 'fr' ? 'Terrain' : 'أرض', icon: MapPin },
    { value: 'commercial', label: language === 'fr' ? 'Local commercial' : 'محل تجاري', icon: Building2 },
    { value: 'bureau', label: language === 'fr' ? 'Bureau' : 'مكتب', icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="add-property-page">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 gap-2">
            <ArrowLeft className="w-4 h-4" />
            {t('common_back')}
          </Button>
          <h1 className="font-manrope text-3xl font-bold text-slate-900">{t('add_title')}</h1>
        </div>

        {/* Progress steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((s, index) => (
            <div key={s.num} className="flex items-center">
              <div 
                className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                  step >= s.num ? 'bg-primary-500 text-white' : 'bg-slate-200 text-slate-500'
                }`}
              >
                {step > s.num ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className={`hidden md:block ml-2 text-sm ${step >= s.num ? 'text-primary-500 font-medium' : 'text-slate-500'}`}>
                {s.label}
              </span>
              {index < steps.length - 1 && (
                <div className={`w-12 md:w-24 h-1 mx-2 ${step > s.num ? 'bg-primary-500' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Type & Transaction */}
        {step === 1 && (
          <Card className="border-0 shadow-lg" data-testid="step-1">
            <CardHeader>
              <CardTitle>{t('add_step1')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Transaction type */}
              <div>
                <Label className="mb-3 block">{language === 'fr' ? 'Type de transaction' : 'نوع المعاملة'}</Label>
                <div className="grid grid-cols-2 gap-4">
                  {['vente', 'location'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`p-4 rounded-xl border-2 transition-all ${
                        formData.transaction_type === type
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleChange('transaction_type', type)}
                    >
                      <span className="font-semibold text-lg">
                        {type === 'vente' ? t('search_buy') : t('search_rent')}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Property type */}
              <div>
                <Label className="mb-3 block">{t('search_type')}</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {propertyTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        formData.property_type === type.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleChange('property_type', type.value)}
                    >
                      <type.icon className={`w-6 h-6 ${formData.property_type === type.value ? 'text-primary-500' : 'text-slate-500'}`} />
                      <span className="text-sm font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <Card className="border-0 shadow-lg" data-testid="step-2">
            <CardHeader>
              <CardTitle>{t('add_step2')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">{t('add_property_title')} *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder={language === 'fr' ? 'Ex: Appartement F3 lumineux avec vue mer' : 'مثال: شقة F3 مشرقة مع إطلالة على البحر'}
                  data-testid="property-title"
                />
              </div>

              <div>
                <Label htmlFor="description">{t('add_description')} *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={language === 'fr' ? 'Décrivez votre bien en détail...' : 'صف عقارك بالتفصيل...'}
                  rows={5}
                  data-testid="property-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">{t('add_price')} (DA) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    placeholder={formData.transaction_type === 'location' ? '50000' : '15000000'}
                    data-testid="property-price"
                  />
                </div>
                <div>
                  <Label htmlFor="surface">{t('search_surface')} (m²) *</Label>
                  <Input
                    id="surface"
                    type="number"
                    value={formData.surface}
                    onChange={(e) => handleChange('surface', e.target.value)}
                    placeholder="100"
                    data-testid="property-surface"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="rooms">{t('search_rooms')}</Label>
                  <Input
                    id="rooms"
                    type="number"
                    value={formData.rooms}
                    onChange={(e) => handleChange('rooms', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="bedrooms">{t('search_bedrooms')}</Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => handleChange('bedrooms', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="bathrooms">{t('prop_bathrooms')}</Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => handleChange('bathrooms', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="floor">{t('prop_floor')}</Label>
                  <Input
                    id="floor"
                    type="number"
                    value={formData.floor}
                    onChange={(e) => handleChange('floor', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="total_floors">{language === 'fr' ? 'Étages total' : 'إجمالي الطوابق'}</Label>
                  <Input
                    id="total_floors"
                    type="number"
                    value={formData.total_floors}
                    onChange={(e) => handleChange('total_floors', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="year_built">{t('prop_year')}</Label>
                  <Input
                    id="year_built"
                    type="number"
                    value={formData.year_built}
                    onChange={(e) => handleChange('year_built', e.target.value)}
                    placeholder="2020"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Label className="mb-3 block">{language === 'fr' ? 'Équipements' : 'المرافق'}</Label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: 'has_parking', label: t('prop_parking'), icon: Car },
                    { key: 'has_elevator', label: t('prop_elevator'), icon: Layers },
                    { key: 'has_balcony', label: t('prop_balcony'), icon: Maximize2 },
                    { key: 'has_garden', label: t('prop_garden'), icon: Home },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center space-x-2">
                      <Checkbox
                        id={item.key}
                        checked={formData[item.key]}
                        onCheckedChange={(checked) => handleChange(item.key, checked)}
                      />
                      <Label htmlFor={item.key} className="flex items-center gap-2 cursor-pointer">
                        <item.icon className="w-4 h-4 text-slate-500" />
                        {item.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Location */}
        {step === 3 && (
          <Card className="border-0 shadow-lg" data-testid="step-3">
            <CardHeader>
              <CardTitle>{t('add_step3')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{language === 'fr' ? 'Wilaya' : 'الولاية'} *</Label>
                <Select value={formData.city} onValueChange={(v) => handleChange('city', v)}>
                  <SelectTrigger data-testid="property-city">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionnez une wilaya' : 'اختر ولاية'} />
                  </SelectTrigger>
                  <SelectContent>
                    {wilayas.map((wilaya) => (
                      <SelectItem key={wilaya.name} value={wilaya.name}>
                        {wilaya.name} {language === 'ar' ? `- ${wilaya.name_ar}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{language === 'fr' ? 'Quartier / Commune' : 'الحي / البلدية'} *</Label>
                <Input
                  value={formData.neighborhood}
                  onChange={(e) => handleChange('neighborhood', e.target.value)}
                  placeholder={language === 'fr' ? 'Ex: Hydra, Kouba, Centre-ville...' : 'مثال: حيدرة، القبة، وسط المدينة...'}
                  data-testid="property-neighborhood"
                />
              </div>

              <div>
                <Label htmlFor="address">{language === 'fr' ? 'Adresse (optionnel)' : 'العنوان (اختياري)'}</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder={language === 'fr' ? 'Rue, numéro...' : 'الشارع، الرقم...'}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Photos */}
        {step === 4 && (
          <Card className="border-0 shadow-lg" data-testid="step-4">
            <CardHeader>
              <CardTitle>{t('add_step4')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  {language === 'fr' 
                    ? 'Ajoutez des photos de votre bien pour attirer plus d\'acheteurs'
                    : 'أضف صور عقارك لجذب المزيد من المشترين'}
                </p>

                {/* Images grid */}
                <div className="grid grid-cols-3 gap-4">
                  {formData.images.map((img, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        onClick={() => handleImageRemove(index)}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 hover:border-primary-500 transition-colors"
                    onClick={handleImageAdd}
                    data-testid="add-image-btn"
                  >
                    <Camera className="w-8 h-8 text-slate-400" />
                    <span className="text-sm text-slate-500">
                      {language === 'fr' ? 'Ajouter' : 'إضافة'}
                    </span>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common_previous')}
          </Button>
          
          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-primary-500 gap-2"
            >
              {t('common_next')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !canProceed()}
              className="bg-secondary-500 hover:bg-secondary-500/90 gap-2"
              data-testid="submit-property"
            >
              {loading ? t('common_loading') : t('add_submit')}
              <Check className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
